import { useState } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../auth/AuthContext";
import { updateUserSettings } from "../../../api/trips";
import { SUPPORTED_LANGUAGES } from "../../../i18n/supportedLanguages";
import { useLocaleSwitch } from "../../../i18n/useLocaleSwitch";
import { localeDefaultDistanceUnit } from "../../../cost/distanceUnit";
import { useSessionPrefsStore } from "../../../store/sessionPrefsStore";
import { OptionLabel } from "./OptionLabel";
import { TOP_CURRENCIES } from "./currencies";

type ModalKind = "language" | "units" | "currency" | null;

export function PreferencesMenuSection({ onSelect }: { onSelect?: () => void }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const switchLocale = useLocaleSwitch();
  const [openModal, setOpenModal] = useState<ModalKind>(null);

  const distanceUnit = useSessionPrefsStore((s) => s.distanceUnit);
  const referenceCurrency = useSessionPrefsStore((s) => s.referenceCurrency);
  const setDistanceUnit = useSessionPrefsStore((s) => s.setDistanceUnit);
  const setReferenceCurrency = useSessionPrefsStore((s) => s.setReferenceCurrency);

  const currentLangLabel = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)?.label ?? i18n.language;

  let unitLabel: string;
  if (distanceUnit === "km") {
    unitLabel = t("settings:preferences.kilometers");
  } else if (distanceUnit === "mile") {
    unitLabel = t("settings:preferences.miles");
  } else {
    unitLabel = t("settings:preferences.autoUnit", { unit: localeDefaultDistanceUnit() });
  }

  function handleLanguageChange(code: string) {
    switchLocale(code);
    if (user) void updateUserSettings({ language: code });
    setOpenModal(null);
    onSelect?.();
  }

  function handleUnitsChange(unit: "km" | "mile" | null) {
    setDistanceUnit(unit);
    if (user) void updateUserSettings({ distanceUnit: unit });
    setOpenModal(null);
    onSelect?.();
  }

  function handleCurrencyChange(currency: string) {
    setReferenceCurrency(currency);
    if (user) void updateUserSettings({ referenceCurrency: currency });
    setOpenModal(null);
    onSelect?.();
  }

  return (
    <>
      <div className="topnav-dropdown__divider" />
      <div className="topnav-dropdown__heading">{t("common:nav.language")}</div>
      <button
        type="button"
        data-testid="pref-language"
        className="topnav-menu-item"
        onClick={() => setOpenModal("language")}
      >
        {t("common:nav.prefsLanguage", { value: currentLangLabel })}
      </button>
      <button
        type="button"
        data-testid="pref-units"
        className="topnav-menu-item"
        onClick={() => setOpenModal("units")}
      >
        {t("common:nav.prefsUnits", { value: unitLabel })}
      </button>
      <button
        type="button"
        data-testid="pref-currency"
        className="topnav-menu-item"
        onClick={() => setOpenModal("currency")}
      >
        {t("common:nav.prefsCurrency", { value: referenceCurrency })}
      </button>

      <Modal
        open={openModal === "language"}
        onCancel={() => setOpenModal(null)}
        footer={null}
        title={t("common:nav.language")}
        destroyOnHidden
      >
        <div data-testid="language-modal">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              data-testid={`lang-modal-option-${lang.code}`}
              className="topnav-menu-item"
              onClick={() => handleLanguageChange(lang.code)}
            >
              <OptionLabel active={i18n.language === lang.code} label={lang.label} />
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        open={openModal === "units"}
        onCancel={() => setOpenModal(null)}
        footer={null}
        title={t("common:nav.units")}
        destroyOnHidden
      >
        <div data-testid="units-modal">
          {([
            { key: "auto", label: t("settings:preferences.autoUnit", { unit: localeDefaultDistanceUnit() }), value: null },
            { key: "km", label: t("settings:preferences.kilometers"), value: "km" as const },
            { key: "mile", label: t("settings:preferences.miles"), value: "mile" as const },
          ]).map((opt) => (
            <button
              key={opt.key}
              type="button"
              data-testid={`units-modal-option-${opt.key}`}
              className="topnav-menu-item"
              onClick={() => handleUnitsChange(opt.value)}
            >
              <OptionLabel active={distanceUnit === opt.value} label={opt.label} />
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        open={openModal === "currency"}
        onCancel={() => setOpenModal(null)}
        footer={null}
        title={t("common:nav.currency")}
        destroyOnHidden
      >
        <div data-testid="currency-modal">
          {TOP_CURRENCIES.map((c) => (
            <button
              key={c.code}
              type="button"
              data-testid={`currency-modal-option-${c.code}`}
              className="topnav-menu-item"
              onClick={() => handleCurrencyChange(c.code)}
            >
              <OptionLabel active={referenceCurrency === c.code} label={`${c.code} — ${c.label}`} />
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}
